import React from 'react';

import * as NavConstants from 'constants/NavConstants';
import {getActiveChatId} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import * as net from 'util/Net';

import {AgentTypeError, ChatDraftStatus, getDraftState, setDraftState} from './ChatDraftState';

interface AIInsufficientCreditsErrorResponse {
  responseJSON?: {
    detail?: string;
    title?: string;
  };
  status?: number;
  __suppress_notification?: boolean;
}

function getInsufficientCreditsAgentTypeError(e: unknown): AgentTypeError | null {
  if (!net.isServerRequestError(e)) {
    return null;
  }
  if ((e as AIInsufficientCreditsErrorResponse).status !== 402) {
    return null;
  }
  const responseJSON = (e as AIInsufficientCreditsErrorResponse).responseJSON;
  const title = responseJSON?.title;
  const detail = responseJSON?.detail;
  if (!title || !detail) {
    return null;
  }
  return {
    title,
    detail,
  };
}

export function handleInsufficientCredits(
  e: Error,
  {
    chatSessionId,
    wasDraftStagedInState,
  }: {
    chatSessionId: string | null;
    wasDraftStagedInState: boolean;
  }
): boolean {
  const agentTypeError = getInsufficientCreditsAgentTypeError(e);
  if (!agentTypeError) {
    return false;
  }

  // Signal upwards that we don't need to show an error
  (e as {__suppress_notification?: boolean}).__suppress_notification = true;

  // Only show a banner if the specified draft is currently visible in the AI sidepanel.
  // Otherwise, show a toast notification.
  const originalDraft = getDraftState(chatSessionId);
  const showBanner =
    wasDraftStagedInState &&
    SidepanelStore.getIsOpen() &&
    SidepanelStore.getSection() === NavConstants.SECTIONS.AI &&
    getActiveChatId() === chatSessionId &&
    originalDraft;

  if (!showBanner) {
    const {title, detail} = agentTypeError;
    notifications.warn(<ToastMessage header={title} body={detail} />, {
      duration: 0,
      id: `llm.metering.${title}.${detail}`,
    });
    // Still update draft state so isSubmitErrorMessage is set correctly
    if (originalDraft) {
      setDraftState(chatSessionId, {
        ...originalDraft,
        agentTypeError,
        status: ChatDraftStatus.DRAFT,
        failedUserMessage: originalDraft.message,
      });
    }
    return true;
  }

  const newDraft = {
    ...originalDraft,
    agentTypeError,
    // Return to DRAFT to unlock the UI, the user could select a different agent to continue,
    // e.g. Ask is less credits than Deep Research.
    status: ChatDraftStatus.DRAFT,
    // Also, preserve the user's failed message in the message thread to avoid jarring snapback.
    failedUserMessage: originalDraft.message,
  };
  setDraftState(chatSessionId, newDraft);
  return true;
}
